import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, 
  Users, 
  UserCheck, 
  Clock, 
  Eye, 
  RefreshCw,
  Stethoscope,
  Building,
  Bed,
  MapPin,
  FileText,
  Receipt,
  Search,
  Printer,
  Download,
  ScrollText
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ipdApi } from '@/api/ipd';
import { IPDQueueEntry, IPDDashboardStats } from '@/types/ipd';
import AdmissionPackageEditor from '@/components/ipd/AdmissionPackageEditor';
import { useWebSocket } from '@/hooks/useWebSocket';
import AddQueueModal from '@/components/ipd/AddQueueModal';
import IPDAdmissionForm from '@/components/ipd/IPDAdmissionForm';
import ReceptionistDischargeForm from '@/components/ipd/ReceptionistDischargeForm';
import IPDPatientDocumentUpload from '@/components/ipd/IPDPatientDocumentUpload';
import { maskPhoneDisplay } from '@/utils/stringUtils';
import { ensureDoctorPrefix } from '@/lib/doctorName';
import ChangeIPDDoctorDialog from '@/components/ipd/ChangeIPDDoctorDialog';
import IPDStickerDialog from '@/components/ipd/IPDStickerDialog';
import { Pencil, Trash2, UserRound } from 'lucide-react';
import {
  useCanMutateFrontDeskIpd,
  useFrontDeskBasePath,
  useIsFrontDeskStaff,
} from '@/hooks/useFrontDeskBasePath';

export default function IPDQueuePage({ offlineMode = false }: { offlineMode?: boolean }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const basePath = useFrontDeskBasePath();
  const isFrontDesk = useIsFrontDeskStaff();
  const canMutateIpd = useCanMutateFrontDeskIpd();
  const billingPath = (admissionId: string) => {
    if (isFrontDesk) {
      return offlineMode
        ? `${basePath}/ipd/${admissionId}/offline-billing`
        : `${basePath}/ipd/${admissionId}/billing`;
    }
    return `/doctor/ipd/${admissionId}/billing`;
  };
  const [queueEntries, setQueueEntries] = useState<IPDQueueEntry[]>([]);
  const [activeTab, setActiveTab] = useState<string>(offlineMode ? 'admitted' : 'queued');
  const [enablingOfflineId, setEnablingOfflineId] = useState<string | null>(null);
  const [deletingOfflineId, setDeletingOfflineId] = useState<string | null>(null);
  const [purgingAdmissionId, setPurgingAdmissionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingQueueId, setEditingQueueId] = useState<string | null>(null);
  const [editingPatient, setEditingPatient] = useState<any | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<IPDQueueEntry | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [dashboardStats, setDashboardStats] = useState<IPDDashboardStats | null>(null);
  const [isAdmissionModalOpen, setIsAdmissionModalOpen] = useState(false);
  const [selectedQueueEntry, setSelectedQueueEntry] = useState<IPDQueueEntry | null>(null);
  const [isDischargeModalOpen, setIsDischargeModalOpen] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [selectedAdmissionForDocuments, setSelectedAdmissionForDocuments] = useState<IPDQueueEntry | null>(null);
  const [selectedQueueEntryForDischarge, setSelectedQueueEntryForDischarge] = useState<IPDQueueEntry | null>(null);
  const [openingDischargePdfQueueId, setOpeningDischargePdfQueueId] = useState<string | null>(null);
  const [changeDoctorEntry, setChangeDoctorEntry] = useState<IPDQueueEntry | null>(null);
  const [stickerEntry, setStickerEntry] = useState<IPDQueueEntry | null>(null);
  const [stickerDialogOpen, setStickerDialogOpen] = useState(false);
  const [editAdmissionEntry, setEditAdmissionEntry] = useState<IPDQueueEntry | null>(null);
  const [isEditAdmissionModalOpen, setIsEditAdmissionModalOpen] = useState(false);

  const canEditAdmission =
    user?.role === 'HOSPITAL_ADMIN' || user?.role === 'SUPER_ADMIN';

  // Search filters (patient name, IPD id, mobile, doctor name)
  const [searchPatientName, setSearchPatientName] = useState('');
  const [searchIpdId, setSearchIpdId] = useState('');
  const [searchMobile, setSearchMobile] = useState('');
  const [searchDoctorName, setSearchDoctorName] = useState('');

  // WebSocket connection for real-time updates (disabled for now)
  // To enable: Set VITE_ENABLE_WEBSOCKET=true in .env file
  const { isConnected } = useWebSocket({
    url: (() => {
      if (import.meta.env.VITE_ENABLE_WEBSOCKET === 'true') {
        const apiBase = import.meta.env.VITE_API_BASE_URL || window.location.origin;
        const wsUrlObj = new URL('/api/ipd/ward-monitoring', apiBase);
        wsUrlObj.protocol = wsUrlObj.protocol === 'https:' ? 'wss:' : 'ws:';
        console.log('WebSocket enabled, connecting to:', wsUrlObj.toString());
        return wsUrlObj.toString();
      } else {
        // console.log('WebSocket disabled - set VITE_ENABLE_WEBSOCKET=true to enable');
        return 'ws://disabled'; // Dummy URL when disabled
      }
    })(),
    onMessage: (message) => {
      if (message.type === 'IPD_UPDATE' || message.type === 'WARD_UPDATE' || 
          message.type === 'ADMISSION_UPDATE' || message.type === 'DISCHARGE_UPDATE') {
        // Update the queue list and stats with new data
        fetchQueueEntries();
        fetchDashboardStats();
        toast.success('IPD status updated in real-time');
      }
    },
    onError: (error) => {
      console.error('WebSocket error:', error);
      // Don't show error to user, just log it
    },
    maxReconnectAttempts: import.meta.env.VITE_ENABLE_WEBSOCKET === 'true' ? 3 : 0,
    reconnectInterval: 5000
  });

  // Fetch queue entries
  const fetchQueueEntries = async () => {
    try {
      setIsLoading(true);
      const params: { status?: string } = {};
      
      // Set status based on active tab
      if (activeTab === 'queued') {
        params.status = 'QUEUED';
      } else if (activeTab === 'admitted') {
        params.status = 'ADMITTED';
      } else if (activeTab === 'discharged') {
        params.status = 'DISCHARGED';
      }
      
      const response = await ipdApi.getQueue(params);
      const data = response?.data?.data;
      const list = Array.isArray(data) ? data : [];

      // Filter based on user role
      let filteredEntries = list;
      if (user?.role === 'DOCTOR') {
        filteredEntries = list.filter((entry: IPDQueueEntry) =>
          entry.admission?.assignedDoctor?.id === user.id
        );
      }
      if (offlineMode) {
        filteredEntries = filteredEntries.filter(
          (entry: IPDQueueEntry) => entry.admission?.useOfflineBilling === true
        );
      }

      setQueueEntries(filteredEntries);
    } catch (error) {
      console.error('Error fetching queue entries:', error);
      setQueueEntries([]);
      toast.error('Failed to load IPD queue. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  // Fetch dashboard statistics
  const fetchDashboardStats = async () => {
    try {
      const response = await ipdApi.getDashboardStats();
      setDashboardStats(response?.data?.data ?? null);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setDashboardStats(null);
    }
  };




  // Handle admit patient
  const handleAdmitPatient = (queueEntry: IPDQueueEntry) => {
    setSelectedQueueEntry(queueEntry);
    setIsAdmissionModalOpen(true);
  };

  const openEditQueue = (entry: IPDQueueEntry) => {
    setEditingQueueId(entry.id);
    setEditingPatient(entry.patient as any);
    setIsAddModalOpen(true);
  };

  const handleDeleteQueue = async (entry: IPDQueueEntry) => {
    const ok = window.confirm(`Delete queue entry ${entry.queueNumber}?`);
    if (!ok) return;
    try {
      await ipdApi.deleteQueue(entry.id);
      toast.success('Queue entry deleted');
      fetchQueueEntries();
      fetchDashboardStats();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to delete queue entry');
    }
  };

  // Handle admission success
  const handleAdmissionSuccess = () => {
    // Refresh data to show updated status
    fetchQueueEntries();
    fetchDashboardStats();
    toast.success('Patient admitted successfully!');
  };

  const handleDownloadAdmissionForm = async (entry: IPDQueueEntry) => {
    if (!entry.admission?.id) {
      toast.error('Admission details not found');
      return;
    }
    try {
      const response = await ipdApi.downloadAdmissionFormPDF(entry.admission.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ipd-admission-form-${entry.ipdNumber || entry.queueNumber || entry.admission.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Admission form downloaded');
    } catch (error) {
      console.error('Failed to download admission form:', error);
      toast.error('Failed to download admission form');
    }
  };

  const handlePrintAdmissionForm = async (entry: IPDQueueEntry) => {
    if (!entry.admission?.id) {
      toast.error('Admission details not found');
      return;
    }
    try {
      const response = await ipdApi.downloadAdmissionFormPDF(entry.admission.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) {
        toast.error('Please allow pop-ups to print the admission form');
        window.URL.revokeObjectURL(url);
        return;
      }
      w.onload = () => {
        try {
          w.focus();
          w.print();
        } finally {
          window.URL.revokeObjectURL(url);
        }
      };
    } catch (error) {
      console.error('Failed to print admission form:', error);
      toast.error('Failed to open admission form for printing');
    }
  };

  // Handle discharge patient
  const handleEnableOfflineBilling = async (entry: IPDQueueEntry) => {
    const admissionId = entry.admission?.id;
    if (!admissionId) {
      toast.error('Admission not found');
      return;
    }
    try {
      setEnablingOfflineId(admissionId);
      const res = await ipdApi.enableOfflineBilling(admissionId);
      const oipd = res.data?.data?.offlineIpdNumber;
      toast.success(oipd ? `Offline billing enabled (${oipd})` : 'Offline billing enabled');
      fetchQueueEntries();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message || 'Failed to enable offline billing');
    } finally {
      setEnablingOfflineId(null);
    }
  };

  const handleDeleteOfflineBilling = async (entry: IPDQueueEntry) => {
    const admissionId = entry.admission?.id;
    if (!admissionId) {
      toast.error('Admission not found');
      return;
    }
    const oipd = entry.offlineIpdNumber || 'this OIPD bill';
    const confirmed = window.confirm(
      `Delete offline bill ${oipd}?\n\n` +
        'This removes the patient from the offline IPD queue and deletes only the offline (OIPD) bill.\n' +
        'Medicines, services, and other bill components are NOT deleted.\n' +
        'The main revenue IPD bill is unchanged.'
    );
    if (!confirmed) return;

    try {
      setDeletingOfflineId(admissionId);
      await ipdApi.deleteOfflineBilling(admissionId);
      toast.success('Offline bill deleted — patient removed from offline queue');
      fetchQueueEntries();
      fetchDashboardStats();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message || 'Failed to delete offline bill');
    } finally {
      setDeletingOfflineId(null);
    }
  };

  const handlePurgeAdmission = async (entry: IPDQueueEntry) => {
    const admissionId = entry.admission?.id;
    if (!admissionId) {
      toast.error('Admission not found');
      return;
    }
    const ipdNo = entry.ipdNumber || entry.offlineIpdNumber || '';
    if (!ipdNo) {
      toast.error('IPD number not found — cannot confirm delete');
      return;
    }
    const firstOk = window.confirm(
      `Permanently delete IPD ${ipdNo} (${entry.patient?.name || 'patient'})?\n\n` +
        'This removes the IPD stay, bills, deposits, refunds, pending collections, and patient expenses for this admission.\n' +
        'Billed medicines / OT stock will be returned to inventory.\n' +
        'The patient registration record is kept.\n\n' +
        'This cannot be undone.'
    );
    if (!firstOk) return;
    const typed = window.prompt(`Type the IPD number exactly to confirm delete:\n${ipdNo}`);
    if (typed == null) return;
    if (typed.trim().toUpperCase() !== String(ipdNo).trim().toUpperCase()) {
      toast.error('IPD number did not match — delete cancelled');
      return;
    }
    try {
      setPurgingAdmissionId(admissionId);
      const res = await ipdApi.purgeAdmission(admissionId, typed.trim());
      const data = res.data?.data;
      const units = data?.stockReturnedUnits ?? 0;
      const seqReclaimed = data?.sequencesReclaimed?.ipd;
      const base =
        units > 0
          ? `IPD deleted. ${units} medicine unit(s) returned to inventory.`
          : 'IPD deleted.';
      toast.success(
        seqReclaimed
          ? `${base} IPD number sequence reset — next admission can reuse ${ipdNo}.`
          : base
      );
      fetchQueueEntries();
      fetchDashboardStats();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message || 'Failed to delete IPD admission');
    } finally {
      setPurgingAdmissionId(null);
    }
  };

  const handleDischargePatient = (entry: IPDQueueEntry) => {
    if (!entry.admission) {
      toast.error('No admission data found for this patient');
      return;
    }
    
    setSelectedQueueEntryForDischarge(entry);
    setIsDischargeModalOpen(true);
  };

  // Handle discharge success
  const handleDischargeSuccess = () => {
    fetchQueueEntries();
    fetchDashboardStats();
    toast.success('Patient discharged successfully!');
  };

  /** Open complete discharge summary PDF (loads saved summary then generates PDF). */
  const handleOpenDischargeSummaryPdf = async (entry: IPDQueueEntry) => {
    if (!entry.admission?.id) {
      toast.error('Admission not found');
      return;
    }
    setOpeningDischargePdfQueueId(entry.id);
    try {
      const res = await ipdApi.getDischargeSummary(entry.admission.id);
      const summary = res.data?.data as {
        dischargeDate: string;
        finalDiagnosis: string;
        treatmentSummary: string;
        proceduresPerformed?: string | null;
        medicationsPrescribed: string;
        followUpInstructions: string;
        followUpAt?: string | null;
        followUpReason?: string | null;
        remarks?: string | null;
        doctorSignature?: string | null;
        hospitalStamp?: string | null;
      } | null;
      if (!summary) {
        toast.error('No discharge summary on file');
        return;
      }
      const dischargeDateIso =
        typeof summary.dischargeDate === 'string'
          ? summary.dischargeDate
          : new Date(summary.dischargeDate as unknown as Date).toISOString();
      const pdfRes = await ipdApi.generateDischargeSummaryPDF(
        entry.admission.id,
        {
          dischargeDate: dischargeDateIso,
          finalDiagnosis: summary.finalDiagnosis,
          treatmentSummary: summary.treatmentSummary,
          proceduresPerformed: summary.proceduresPerformed || undefined,
          medicationsPrescribed: summary.medicationsPrescribed,
          followUpInstructions: summary.followUpInstructions,
          followUpAt: summary.followUpAt ?? null,
          followUpReason: summary.followUpReason ?? null,
          remarks: summary.remarks || undefined,
          doctorSignature: summary.doctorSignature || undefined,
          hospitalStamp: summary.hospitalStamp || undefined
        },
        { forPrint: true }
      );
      const blob = new Blob([pdfRes.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) {
        toast.error('Please allow pop-ups to view the discharge summary');
        URL.revokeObjectURL(url);
        return;
      }
      w.onload = () => URL.revokeObjectURL(url);
      toast.success('Opening discharge summary');
    } catch (err) {
      console.error(err);
      toast.error('Failed to open discharge summary');
    } finally {
      setOpeningDischargePdfQueueId(null);
    }
  };

  // Filter entries: each non-empty field must match (AND across filled fields)
  const normalize = (s: string) => (s ?? '').toLowerCase().trim();
  const digitsOnly = (s: string) => (s ?? '').replace(/\D/g, '');
  const hasSearch = searchPatientName || searchIpdId || searchMobile || searchDoctorName;
  const filteredEntries = (queueEntries ?? []).filter((entry) => {
    if (!hasSearch) return true;
    const patientName = normalize(entry.patient?.name ?? '');
    const ipdNum = normalize(
      `${entry.queueNumber ?? ''} ${entry.ipdNumber ?? ''} ${entry.offlineIpdNumber ?? ''}`
    );
    const phone = digitsOnly(entry.patient?.phone ?? '');
    const doctorName = normalize(entry.admission?.assignedDoctor?.name ?? '');
    if (searchPatientName && !patientName.includes(normalize(searchPatientName))) return false;
    if (searchIpdId && !ipdNum.includes(normalize(searchIpdId))) return false;
    if (searchMobile && !phone.includes(digitsOnly(searchMobile))) return false;
    if (searchDoctorName && !doctorName.includes(normalize(searchDoctorName))) return false;
    return true;
  });

  // Get status badge color
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'QUEUED':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'ADMITTED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'DISCHARGED':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Render queue table (for queued patients)
  const renderQueueTable = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="animate-pulse">
            <div className="text-lg text-gray-600">Loading queue entries...</div>
          </div>
        </div>
      );
    }

    if (filteredEntries.length === 0) {
      return (
        <div className="text-center py-8">
          <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {user?.role === 'DOCTOR' ? 'No assigned patients in queue' : 'No patients in queue'}
          </h3>
          <p className="text-gray-500">
            {user?.role === 'DOCTOR' 
              ? 'No patients assigned to you are currently in the IPD queue.' 
              : (searchPatientName || searchIpdId || searchMobile || searchDoctorName)
                ? 'No queue entries match your search.'
                : 'No IPD queue entries found for the selected criteria.'
            }
          </p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Queue #</TableHead>
              <TableHead>Patient Name</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(filteredEntries ?? []).map((entry) => (
              <TableRow key={entry.id}>
                <TableCell className="font-medium">{entry.queueNumber}</TableCell>
                <TableCell>
                  <div>
                    <div className="font-medium">{entry.patient?.name ?? '—'}</div>
                  </div>
                </TableCell>
                <TableCell>
                  {new Date(entry.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                </TableCell>
                <TableCell>
                  <Badge className={getStatusBadgeColor(entry.status)}>
                    {entry.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedEntry(entry);
                        setIsDetailsModalOpen(true);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    {canMutateIpd ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditQueue(entry)}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void handleDeleteQueue(entry)}
                        >
                          <Trash2 className="h-4 w-4 mr-1 text-rose-600" />
                          Delete
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleAdmitPatient(entry)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Stethoscope className="h-4 w-4 mr-1" />
                          Admit
                        </Button>
                      </>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  // Render admitted table (for admitted patients)
  const renderAdmittedTable = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="animate-pulse">
            <div className="text-lg text-gray-600">Loading admitted patients...</div>
          </div>
        </div>
      );
    }

    if (filteredEntries.length === 0) {
      return (
        <div className="text-center py-8">
          <UserCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {user?.role === 'DOCTOR' ? 'No assigned admitted patients' : 'No admitted patients'}
          </h3>
          <p className="text-gray-500">
            {user?.role === 'DOCTOR' 
              ? 'No patients assigned to you are currently admitted.' 
              : (searchPatientName || searchIpdId || searchMobile || searchDoctorName)
                ? 'No admitted patients match your search.'
                : 'No admitted patients found for the selected criteria.'
            }
          </p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{offlineMode ? 'OIPD #' : 'IPD #'}</TableHead>
              <TableHead>Patient Name</TableHead>
              <TableHead>Doctor</TableHead>
              <TableHead>Ward Details</TableHead>
              <TableHead>Admitted At</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(filteredEntries ?? []).map((entry) => (
              <TableRow key={entry.id}>
                <TableCell className="font-medium">
                  {offlineMode ? (entry.offlineIpdNumber || '—') : (entry.ipdNumber || '—')}
                </TableCell>
                <TableCell>
                  <div>
                    <div className="font-medium">{entry.patient?.name ?? '—'}</div>
                  </div>
                </TableCell>
                <TableCell>
                  {entry.admission?.assignedDoctor ? (
                    <div>
                      <div className="font-medium">{ensureDoctorPrefix(entry.admission.assignedDoctor.name)}</div>
                      <div className="text-sm text-artis-slate">{entry.admission.assignedDoctor.specialisation}</div>
                      {canMutateIpd && entry.admission?.id && (
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-xs text-teal-700"
                          onClick={() => setChangeDoctorEntry(entry)}
                        >
                          <UserRound className="h-3 w-3 mr-1 inline" />
                          Change doctor
                        </Button>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400">Not assigned</span>
                  )}
                </TableCell>
                <TableCell>
                  {entry.admission ? (
                    entry.admission.thirdPartyHospitalName ? (
                      <div className="space-y-1">
                        <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 border border-amber-200">
                          Third party: {entry.admission.thirdPartyHospitalName}
                        </span>
                      </div>
                    ) : (
                    <div className="space-y-1">
                      <div className="flex items-center gap-1">
                        <Building className="h-3 w-3 text-blue-600" />
                        <span className="text-sm font-medium">{entry.admission.wardType}</span>
                      </div>
                      {entry.admission.roomNumber && (
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-green-600" />
                          <span className="text-sm text-gray-600">Room: {entry.admission.roomNumber}</span>
                        </div>
                      )}
                      {entry.admission.bedNumber && (
                        <div className="flex items-center gap-1">
                          <Bed className="h-3 w-3 text-purple-600" />
                          <span className="text-sm text-gray-600">Bed: {entry.admission.bedNumber}</span>
                        </div>
                      )}
                    </div>
                    )
                  ) : (
                    <span className="text-gray-400">No ward assigned</span>
                  )}
                </TableCell>
                <TableCell>
                  {entry.admission?.admissionDate 
                    ? new Date(entry.admission.admissionDate).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                    : new Date(entry.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                  }
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedEntry(entry);
                        setIsDetailsModalOpen(true);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    {canEditAdmission && entry.admission?.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditAdmissionEntry(entry);
                          setIsEditAdmissionModalOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit admission
                      </Button>
                    )}
                    {offlineMode && canEditAdmission && entry.admission?.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-700 border-red-200 hover:bg-red-50"
                        disabled={deletingOfflineId === entry.admission.id}
                        onClick={() => handleDeleteOfflineBilling(entry)}
                        title="Delete offline (OIPD) bill only — medicines and services are kept"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        {deletingOfflineId === entry.admission.id ? 'Deleting…' : 'Delete offline bill'}
                      </Button>
                    )}
                    {canEditAdmission && entry.admission?.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-800 border-red-300 hover:bg-red-50"
                        disabled={purgingAdmissionId === entry.admission.id}
                        onClick={() => handlePurgeAdmission(entry)}
                        title="Permanently delete this IPD stay — bills/expenses removed; medicines restocked"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        {purgingAdmissionId === entry.admission.id ? 'Deleting…' : 'Delete IPD'}
                      </Button>
                    )}
                    {entry.admission?.id && isFrontDesk && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`${basePath}/ipd/${entry.admission!.id}`)}
                        >
                          <Stethoscope className="h-4 w-4 mr-1" />
                          Patient detail
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(billingPath(entry.admission!.id))}
                        >
                          <Receipt className="h-4 w-4 mr-1" />
                          {offlineMode ? 'Offline billing' : 'Billing'}
                        </Button>
                        {canMutateIpd && !offlineMode && !entry.admission?.useOfflineBilling && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={enablingOfflineId === entry.admission!.id}
                            onClick={() => handleEnableOfflineBilling(entry)}
                            title="Insurance / settlement parallel bill (OIPD)"
                          >
                            <Receipt className="h-4 w-4 mr-1" />
                            {enablingOfflineId === entry.admission!.id ? 'Enabling…' : 'Enable offline'}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePrintAdmissionForm(entry)}
                        >
                          <Printer className="h-4 w-4 mr-1" />
                          Print Form
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadAdmissionForm(entry)}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Admission Form
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedAdmissionForDocuments(entry);
                        setIsDocumentModalOpen(true);
                      }}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Documents
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setStickerEntry(entry);
                        setStickerDialogOpen(true);
                      }}
                      title="Print file stickers"
                    >
                      <Printer className="h-4 w-4 mr-1" />
                      Stickers
                    </Button>
                    {canMutateIpd ? (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleDischargePatient(entry)}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        <Users className="h-4 w-4 mr-1" />
                        Discharge
                      </Button>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  // Render discharged table (for discharged patients)
  const renderDischargedTable = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="animate-pulse">
            <div className="text-lg text-gray-600">Loading discharged patients...</div>
          </div>
        </div>
      );
    }

    if (filteredEntries.length === 0) {
      return (
        <div className="text-center py-8">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {user?.role === 'DOCTOR' ? 'No assigned discharged patients' : 'No discharged patients'}
          </h3>
          <p className="text-gray-500">
            {user?.role === 'DOCTOR' 
              ? 'No patients assigned to you have been discharged recently.' 
              : (searchPatientName || searchIpdId || searchMobile || searchDoctorName)
                ? 'No discharged patients match your search.'
                : 'No discharged patients found for the selected criteria.'
            }
          </p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>IPD #</TableHead>
              <TableHead>Patient Name</TableHead>
              <TableHead>Doctor</TableHead>
              <TableHead>Ward Details</TableHead>
              <TableHead>Discharged At</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(filteredEntries ?? []).map((entry) => (
              <TableRow key={entry.id}>
                <TableCell className="font-medium">{entry.ipdNumber || '—'}</TableCell>
                <TableCell>
                  <div>
                    <div className="font-medium">{entry.patient?.name ?? '—'}</div>
                    {entry.admission?.dischargeSummary?.id ? (
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <Badge
                          variant="outline"
                          className="text-xs font-normal text-green-800 border-green-200 bg-green-50"
                        >
                          <ScrollText className="h-3 w-3 mr-1 inline" />
                          Complete discharge summary
                        </Badge>
                        <button
                          type="button"
                          className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                          disabled={openingDischargePdfQueueId === entry.id}
                          onClick={() => handleOpenDischargeSummaryPdf(entry)}
                        >
                          {openingDischargePdfQueueId === entry.id ? 'Opening…' : 'View / print PDF'}
                        </button>
                      </div>
                    ) : (
                      <p className="text-xs text-amber-700 mt-1.5">No discharge summary on file</p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {entry.admission?.assignedDoctor ? (
                    <div>
                      <div className="font-medium">{ensureDoctorPrefix(entry.admission.assignedDoctor.name)}</div>
                      <div className="text-sm text-artis-slate">{entry.admission.assignedDoctor.specialisation}</div>
                    </div>
                  ) : (
                    <span className="text-gray-400">Not assigned</span>
                  )}
                </TableCell>
                <TableCell>
                  {entry.admission ? (
                    entry.admission.thirdPartyHospitalName ? (
                      <div className="space-y-1">
                        <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 border border-amber-200">
                          Third party: {entry.admission.thirdPartyHospitalName}
                        </span>
                      </div>
                    ) : (
                    <div className="space-y-1">
                      <div className="flex items-center gap-1">
                        <Building className="h-3 w-3 text-blue-600" />
                        <span className="text-sm font-medium">{entry.admission.wardType}</span>
                      </div>
                      {entry.admission.roomNumber && (
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-green-600" />
                          <span className="text-sm text-gray-600">Room: {entry.admission.roomNumber}</span>
                        </div>
                      )}
                      {entry.admission.bedNumber && (
                        <div className="flex items-center gap-1">
                          <Bed className="h-3 w-3 text-purple-600" />
                          <span className="text-sm text-gray-600">Bed: {entry.admission.bedNumber}</span>
                        </div>
                      )}
                    </div>
                    )
                  ) : (
                    <span className="text-gray-400">No ward assigned</span>
                  )}
                </TableCell>
                <TableCell>
                  {entry.admission?.dischargeDate 
                    ? new Date(entry.admission.dischargeDate).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                    : entry.dischargedAt 
                    ? new Date(entry.dischargedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                    : 'N/A'
                  }
                </TableCell>
				<TableCell>
                  <div className="flex items-center gap-2 flex-wrap">
                    {canEditAdmission && entry.admission?.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditAdmissionEntry(entry);
                          setIsEditAdmissionModalOpen(true);
                        }}
                        title="Edit admission form (admin)"
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit admission
                      </Button>
                    )}
                    {offlineMode && canEditAdmission && entry.admission?.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-700 border-red-200 hover:bg-red-50"
                        disabled={deletingOfflineId === entry.admission.id}
                        onClick={() => handleDeleteOfflineBilling(entry)}
                        title="Delete offline (OIPD) bill only — medicines and services are kept"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        {deletingOfflineId === entry.admission.id ? 'Deleting…' : 'Delete offline bill'}
                      </Button>
                    )}
                    {canEditAdmission && entry.admission?.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-800 border-red-300 hover:bg-red-50"
                        disabled={purgingAdmissionId === entry.admission.id}
                        onClick={() => handlePurgeAdmission(entry)}
                        title="Permanently delete this IPD stay — bills/expenses removed; medicines restocked"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        {purgingAdmissionId === entry.admission.id ? 'Deleting…' : 'Delete IPD'}
                      </Button>
                    )}
                    {entry.admission?.id && isFrontDesk && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`${basePath}/ipd/${entry.admission!.id}`)}
                        >
                          <Stethoscope className="h-4 w-4 mr-1" />
                          Patient detail
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(billingPath(entry.admission!.id))}
                        >
                          <Receipt className="h-4 w-4 mr-1" />
                          Billing
                        </Button>
                        {canMutateIpd && entry.admission.dischargeSummary?.id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedQueueEntryForDischarge(entry);
                              setIsDischargeModalOpen(true);
                            }}
                          >
                            <ScrollText className="h-4 w-4 mr-1" />
                            Edit discharge summary
                          </Button>
                        )}
                        {entry.admission.dischargeSummary?.id && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={openingDischargePdfQueueId === entry.id}
                            onClick={() => handleOpenDischargeSummaryPdf(entry)}
                          >
                            <ScrollText className="h-4 w-4 mr-1" />
                            {openingDischargePdfQueueId === entry.id ? 'Opening…' : 'Discharge summary'}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePrintAdmissionForm(entry)}
                        >
                          <Printer className="h-4 w-4 mr-1" />
                          Print Form
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadAdmissionForm(entry)}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Admission Form
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedEntry(entry);
                        setIsDetailsModalOpen(true);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  // Use real dashboard stats or fallback to calculated stats
  const stats = dashboardStats ? {
    inQueue: dashboardStats.totalQueued,
    admitted: dashboardStats.totalAdmitted,
    discharged: dashboardStats.totalDischarged,
  } : {
    inQueue: (queueEntries ?? []).filter(entry => entry.status === 'QUEUED').length,
    admitted: (queueEntries ?? []).filter(entry => entry.status === 'ADMITTED').length,
    discharged: (queueEntries ?? []).filter(entry => entry.status === 'DISCHARGED').length,
  };

  useEffect(() => {
    if (user?.id) {
      fetchQueueEntries();
      fetchDashboardStats();
    }
  }, [activeTab, user?.id]);

  return (
    <div className="min-h-[60vh] bg-artis-cream p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {!canMutateIpd && user?.role === 'OPERATIONS_MANAGER' ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            View only — Operations Manager can browse IPD queue, billing, and documents; admitting, discharging, and queue edits require reception or admin.
          </div>
        ) : null}
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="thms-page-title flex items-center gap-2">
              <Users className="h-8 w-8 text-blue-600" />
              {offlineMode
                ? 'IPD Offline Billing'
                : user?.role === 'DOCTOR'
                  ? 'My IPD Patients'
                  : 'Queue Management'}
            </h1>
            <p className="thms-page-subtitle">
              {offlineMode
                ? 'Insurance / reimbursement settlement bills (OIPD) — excluded from revenue reports'
                : user?.role === 'DOCTOR'
                  ? `Patients assigned to ${ensureDoctorPrefix(user.name)}`
                  : 'Manage today\'s IPD patient queue and confirmations'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {canMutateIpd && user?.role !== 'DOCTOR' && !offlineMode && (
              <Button
                onClick={() => setIsAddModalOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg"
                size="lg"
              >
                <Plus className="mr-2 h-5 w-5" />
                Add Queue
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-yellow-50 border-yellow-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-yellow-800">In Queue</CardTitle>
              <Clock className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-900">{stats.inQueue}</div>
              <p className="text-xs text-yellow-700">Patients waiting</p>
            </CardContent>
          </Card>

          <Card className="bg-blue-50 border-blue-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-blue-800">Admitted</CardTitle>
              <UserCheck className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-900">{stats.admitted}</div>
              <p className="text-xs text-blue-700">Currently admitted</p>
            </CardContent>
          </Card>

          <Card className="bg-green-50 border-green-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-green-800">Discharged</CardTitle>
              <Users className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-900">{stats.discharged}</div>
              <p className="text-xs text-green-700">Recently discharged</p>
            </CardContent>
          </Card>
        </div>

        {/* Controls */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">
                  {offlineMode ? 'Offline billing' : 'IPD Management'}
                </h3>
                <Badge variant="outline" className="text-sm">
                  {activeTab === 'queued' && 'Queue Management'}
                  {activeTab === 'admitted' && 'Admitted Patients'}
                  {activeTab === 'discharged' && 'Discharged Patients'}
                </Badge>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  fetchQueueEntries();
                  fetchDashboardStats();
                }}
                disabled={isLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* IPD Management Tabs */}
        <Card>
          <CardContent className="p-0">
            {/* Search section: patient name, IPD id, mobile, doctor name */}
            <div className="p-4 border-b bg-gray-50/80">
              <div className="flex items-center gap-2 mb-3">
                <Search className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Search</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Patient name</label>
                  <Input
                    placeholder="Search by name"
                    value={searchPatientName}
                    onChange={(e) => setSearchPatientName(e.target.value)}
                    className="h-9"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">IPD #</label>
                  <Input
                    placeholder="e.g. IPD524211495"
                    value={searchIpdId}
                    onChange={(e) => setSearchIpdId(e.target.value)}
                    className="h-9"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Mobile number</label>
                  <Input
                    placeholder="e.g. 7011733681"
                    value={searchMobile}
                    onChange={(e) => setSearchMobile(e.target.value)}
                    className="h-9"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Doctor name</label>
                  <Input
                    placeholder="Search by doctor"
                    value={searchDoctorName}
                    onChange={(e) => setSearchDoctorName(e.target.value)}
                    className="h-9"
                  />
                </div>
              </div>
              {(searchPatientName || searchIpdId || searchMobile || searchDoctorName) && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchPatientName('');
                    setSearchIpdId('');
                    setSearchMobile('');
                    setSearchDoctorName('');
                  }}
                  className="mt-2 text-sm text-blue-600 hover:underline"
                >
                  Clear search
                </button>
              )}
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className={`grid w-full ${offlineMode ? 'grid-cols-2' : 'grid-cols-3'}`}>
                {!offlineMode && (
                <TabsTrigger value="queued" className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Queue ({stats.inQueue})
                </TabsTrigger>
                )}
                <TabsTrigger value="admitted" className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4" />
                  Admitted ({stats.admitted})
                </TabsTrigger>
                <TabsTrigger value="discharged" className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Discharged ({stats.discharged})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="queued" className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">
                      {user?.role === 'DOCTOR' ? 'My Assigned Patients' : 'Queue Management'}
                    </h3>
                    {canMutateIpd && user?.role !== 'DOCTOR' && (
                      <Button
                        onClick={() => setIsAddModalOpen(true)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add to Queue
                      </Button>
                    )}
                  </div>
                  {renderQueueTable()}
                </div>
              </TabsContent>

              <TabsContent value="admitted" className="p-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Admitted Patients</h3>
                  {renderAdmittedTable()}
                </div>
              </TabsContent>

              <TabsContent value="discharged" className="p-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Discharged Patients</h3>
                  {renderDischargedTable()}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* WebSocket Status */}
        <div className="flex items-center justify-center">
          <div className={`flex items-center gap-2 text-sm ${
            import.meta.env.VITE_ENABLE_WEBSOCKET === 'true' 
              ? (isConnected ? 'text-green-600' : 'text-red-600')
              : 'text-gray-500'
          }`}>
            <div className={`w-2 h-2 rounded-full ${
              import.meta.env.VITE_ENABLE_WEBSOCKET === 'true'
                ? (isConnected ? 'bg-green-500' : 'bg-red-500')
                : 'bg-gray-400'
            }`}></div>
            {import.meta.env.VITE_ENABLE_WEBSOCKET === 'true' 
              ? (isConnected ? 'Connected to real-time updates' : 'Disconnected from real-time updates')
              : 'Real-time updates disabled (set VITE_ENABLE_WEBSOCKET=true to enable)'
            }
          </div>
        </div>
      </div>

      {/* Add Queue Modal */}
      <AddQueueModal
        isOpen={isAddModalOpen}
        editQueueId={editingQueueId}
        initialPatient={editingPatient}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingQueueId(null);
          setEditingPatient(null);
        }}
        onSuccess={() => {
          fetchQueueEntries();
          fetchDashboardStats();
          setEditingQueueId(null);
          setEditingPatient(null);
        }}
      />

        {/* IPD Admission Form Modal */}
        <IPDAdmissionForm
          isOpen={isAdmissionModalOpen}
          onClose={() => setIsAdmissionModalOpen(false)}
          onSuccess={handleAdmissionSuccess}
          queueEntry={selectedQueueEntry}
        />

        <IPDAdmissionForm
          isOpen={isEditAdmissionModalOpen}
          onClose={() => {
            setIsEditAdmissionModalOpen(false);
            setEditAdmissionEntry(null);
          }}
          onSuccess={() => {
            fetchQueueEntries();
            fetchDashboardStats();
            setIsEditAdmissionModalOpen(false);
            setEditAdmissionEntry(null);
          }}
          queueEntry={editAdmissionEntry}
          editAdmissionId={editAdmissionEntry?.admission?.id ?? null}
        />

        {/* Discharge Modal */}
        <ReceptionistDischargeForm
          isOpen={isDischargeModalOpen}
          onClose={() => setIsDischargeModalOpen(false)}
          onSuccess={handleDischargeSuccess}
          queueEntry={selectedQueueEntryForDischarge}
          billTrack={
            offlineMode || selectedQueueEntryForDischarge?.admission?.useOfflineBilling
              ? 'OFFLINE'
              : 'REVENUE'
          }
        />

      {/* Queue Details Modal (View: all queue + admission details; package/surgery editable) */}
      <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Queue Entry Details</DialogTitle>
          </DialogHeader>
          {selectedEntry && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Queue Number</label>
                  <p className="text-lg font-semibold">{selectedEntry.queueNumber}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <Badge className={getStatusBadgeColor(selectedEntry.status)}>
                    {selectedEntry.status}
                  </Badge>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Patient Information</label>
                <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium">{selectedEntry.patient?.name ?? '—'}</p>
                  <p className="text-sm text-gray-600">UHID: {selectedEntry.patient?.uhid ?? '—'}</p>
                  <p className="text-sm text-gray-600">Phone: {selectedEntry.patient?.phone ? maskPhoneDisplay(selectedEntry.patient.phone) : '—'}</p>
                  <p className="text-sm text-gray-600">Gender: {selectedEntry.patient?.gender ?? '—'}</p>
                </div>
              </div>

              {selectedEntry.admission?.assignedDoctor && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Assigned Doctor</label>
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium">{ensureDoctorPrefix(selectedEntry.admission.assignedDoctor.name)}</p>
                    <p className="text-sm text-gray-600">{selectedEntry.admission.assignedDoctor.specialisation}</p>
                  </div>
                </div>
              )}

              {selectedEntry.admission && (
                <>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Admission Details</label>
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-2">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium">Admission Date</p>
                          <p className="text-sm text-gray-600">
                            {new Date(selectedEntry.admission.admissionDate).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Ward Type</p>
                          <p className="text-sm text-gray-600">
                            {selectedEntry.admission.thirdPartyHospitalName
                              ? '—'
                              : selectedEntry.admission.wardType}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Room / Bed</p>
                          <p className="text-sm text-gray-600">
                            {selectedEntry.admission.thirdPartyHospitalName
                              ? '—'
                              : ([selectedEntry.admission.roomNumber, selectedEntry.admission.bedNumber].filter(Boolean).join(' / ') || '—')}
                          </p>
                        </div>
                        {selectedEntry.admission.thirdPartyHospitalName ? (
                          <div className="col-span-2">
                            <p className="text-sm font-medium">Third party hospital</p>
                            <p className="text-sm">
                              <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 border border-amber-200">
                                Third party: {selectedEntry.admission.thirdPartyHospitalName}
                              </span>
                            </p>
                          </div>
                        ) : null}
                        <div>
                          <p className="text-sm font-medium">Insurance</p>
                          <p className="text-sm text-gray-600">
                            {selectedEntry.admission.insuranceType !== 'NA'
                              ? `${selectedEntry.admission.insuranceCompany ?? ''} (${selectedEntry.admission.insuranceType})`
                              : 'No Insurance'}
                          </p>
                        </div>
                        {selectedEntry.admission.policyNumber && (
                          <div>
                            <p className="text-sm font-medium">Policy Number</p>
                            <p className="text-sm text-gray-600">{selectedEntry.admission.policyNumber}</p>
                          </div>
                        )}
                        {selectedEntry.admission.tpaName && (
                          <div>
                            <p className="text-sm font-medium">TPA</p>
                            <p className="text-sm text-gray-600">{selectedEntry.admission.tpaName}</p>
                          </div>
                        )}
                      </div>
                      {selectedEntry.admission.chiefComplaint && (
                        <div>
                          <p className="text-sm font-medium">Chief Complaint</p>
                          <p className="text-sm text-gray-600">{selectedEntry.admission.chiefComplaint}</p>
                        </div>
                      )}
                      {selectedEntry.admission.admissionNotes && (
                        <div>
                          <p className="text-sm font-medium">Admission Notes</p>
                          <p className="text-sm text-gray-600">{selectedEntry.admission.admissionNotes}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {canMutateIpd &&
                    selectedEntry.status === 'ADMITTED' &&
                    selectedEntry.admission?.id && (
                    <div className="border-t pt-4">
                      <label className="text-sm font-medium text-gray-500 block mb-2">
                        Package & Surgery (editable while admitted)
                      </label>
                      <AdmissionPackageEditor
                        admissionId={selectedEntry.admission.id}
                        initial={{
                          surgeryName: selectedEntry.admission.surgeryName,
                          totalPackageCost: selectedEntry.admission.totalPackageCost,
                          packageType: selectedEntry.admission.packageType,
                          gstApplicable: selectedEntry.admission.gstApplicable,
                        }}
                        showGst={false}
                        onSaved={() => {
                          fetchQueueEntries();
                        }}
                      />
                    </div>
                  )}
                </>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Created At</label>
                  <p className="text-sm">{new Date(selectedEntry.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Created By</label>
                  <p className="text-sm">{selectedEntry.createdBy?.name ?? '—'}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Upload Modal */}
      <Dialog open={isDocumentModalOpen} onOpenChange={setIsDocumentModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Patient Documents - {selectedAdmissionForDocuments?.patient.name}
            </DialogTitle>
          </DialogHeader>
          {selectedAdmissionForDocuments?.admission && (
            <IPDPatientDocumentUpload
              admissionId={selectedAdmissionForDocuments.admission.id}
              patientName={selectedAdmissionForDocuments.patient.name}
              onDocumentUploaded={() => {
                // Optionally refresh data or show success message
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <ChangeIPDDoctorDialog
        open={!!changeDoctorEntry}
        onOpenChange={(open) => {
          if (!open) setChangeDoctorEntry(null);
        }}
        entry={changeDoctorEntry}
        onDoctorChanged={(updated) => {
          setQueueEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
          setChangeDoctorEntry(null);
          setStickerEntry(updated);
          setStickerDialogOpen(true);
        }}
      />

      <IPDStickerDialog
        open={stickerDialogOpen}
        onOpenChange={(open) => {
          setStickerDialogOpen(open);
          if (!open) setStickerEntry(null);
        }}
        entry={stickerEntry}
      />
    </div>
  );
}
